import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header.tsx";
import Footer from "@/components/layout/Footer.tsx";
import { useAuth } from "@/contexts/AuthContext.tsx";
import { getPublicNotices } from "@/api/csApi";

// 💡 1. 메인 탭 (대분류)
const MAIN_TABS = [
  { id: "FAQ", label: "자주 묻는 질문" },
  { id: "NOTICE", label: "공지사항" }
];

// 💡 2. FAQ 서브 카테고리 (소분류)
const FAQ_CATEGORIES = ["전체", "배송안내", "주문/결제", "취소/교환/반품", "식물관리"];

const FAQ_DATA = [
  { id: 1, category: "배송안내", question: "원하는 날짜와 시간에 배송 지정이 가능한가요?", answer: "네, 가능합니다. 주문/결제 화면에서 '희망 배송일'과 '오전/오후'를 선택하실 수 있습니다. 단, 생물 특성상 교통 상황에 따라 정확한 시간(예: 12시 30분) 지정은 어려우며, 특정 기념일(어버이날 등)에는 배송 물량이 많아 시간 지정이 제한될 수 있습니다." },
  { id: 2, category: "식물관리", question: "꽃을 배송받았는데 조금 시들어 보여요.", answer: "배송 과정에서 수분이 부족해 일시적으로 생기를 잃었을 수 있습니다. 줄기 끝을 사선으로 1~2cm 정도 자른 후, 차가운 물이 담긴 화병에 꽂아 서늘한 곳에 반나절 정도 두시면 금방 다시 생기를 되찾습니다. 만약 하루가 지나도 회복되지 않는다면 1:1 문의로 사진을 남겨주세요." },
  { id: 3, category: "주문/결제", question: "축하 화환의 리본 문구는 어떻게 입력하나요?", answer: "화환 상품을 장바구니에 담기 전, 상품 상세 페이지의 [옵션 선택] 란에서 '경조사어(우측 리본)'와 '보내는 분(좌측 리본)' 문구를 직접 입력하실 수 있습니다." },
  { id: 4, category: "취소/교환/반품", question: "주문 결제 후 취소나 환불이 가능한가요?", answer: "꽃과 식물은 '생물'이므로 상품이 제작 단계에 들어가거나 배송이 시작된 이후에는 단순 변심으로 인한 취소 및 환불이 불가능합니다. 취소를 원하실 경우, 반드시 '결제 완료' 또는 '상품 준비 중' 상태에서 고객센터로 즉시 연락해 주시기 바랍니다." },
  { id: 5, category: "식물관리", question: "화병의 물은 며칠마다 갈아주어야 하나요?", answer: "여름철에는 매일, 겨울철에는 2~3일에 한 번씩 차가운 물로 갈아주시는 것이 좋습니다. 물을 갈아주실 때 화병 안쪽을 깨끗이 씻고, 꽃의 줄기 끝을 사선으로 조금씩 다시 잘라주시면 꽃을 훨씬 더 오래 보실 수 있습니다." }
];

interface Notice {
  noticeId: number;
  title: string;
  content: string;
  isPinned: string;
  createdAt: string;
}

export default function CustomerCenterPage() {
  const navigate = useNavigate();
  const { isLoggedIn, openLoginModal } = useAuth();

  // 💡 상태 관리 세분화 (메인 탭 + FAQ 서브 탭)
  const [mainTab, setMainTab] = useState("FAQ");
  const [faqCategory, setFaqCategory] = useState("전체");
  const [openId, setOpenId] = useState<number | null>(null);

  const [notices, setNotices] = useState<Notice[]>([]);
  const [isLoadingNotices, setIsLoadingNotices] = useState(false);

  useEffect(() => {
    // 메인 탭이 공지사항일 때만 데이터를 불러오도록 최적화
    if (mainTab === "NOTICE" && notices.length === 0) {
      const fetchNotices = async () => {
        try {
          setIsLoadingNotices(true);
          const res = await getPublicNotices(0, 20);
          const responseData = res.data || res;
          setNotices(responseData.items || []);
        } catch (error) {
          console.error("공지사항 조회 오류:", error);
        } finally {
          setIsLoadingNotices(false);
        }
      };
      fetchNotices();
    }
  }, [mainTab, notices.length]);

  const filteredFaqs = faqCategory === "전체"
    ? FAQ_DATA
    : FAQ_DATA.filter((faq) => faq.category === faqCategory);

  const toggleAccordion = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  const handleInquiryClick = () => {
    if (isLoggedIn) {
      navigate("/cs/inquiry");
    } else {
      alert("로그인이 필요한 서비스입니다.");
      openLoginModal("/cs/inquiry");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <main className="flex-1 mx-auto max-w-4xl px-4 py-12 lg:px-6 mb-20 w-full">
        <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6 mb-20">

          <div className="mb-10 text-center">
            <h2 className="text-3xl font-black tracking-tight text-slate-800">고객센터</h2>
          </div>

          <div className="mb-12 flex flex-col items-center justify-center gap-4 rounded-2xl bg-slate-50 py-8 border border-slate-100 sm:flex-row sm:gap-12 text-center sm:text-left">
            <div>
              <p className="text-sm font-bold text-slate-500">고객센터 대표번호</p>
              {/*<p className="text-2xl font-black text-rose-600">1588-0000</p>*/}
              <p className="text-2xl font-black text-rose-600">010-2890-3655</p>
            </div>
            <div className="hidden h-12 w-px bg-slate-200 sm:block"></div>
            <div className="text-sm text-slate-600">
              {/*<p>평일 09:00 ~ 18:00 (점심시간 12:00 ~ 13:00)</p>*/}
              {/*<p>주말 및 공휴일 휴무</p>*/}
              <p>24시간 대기</p>
            </div>
          </div>

          {/* 💡 대분류 탭 (쿠팡 스타일: 하단 굵은 보더) */}
          <div className="flex border-b border-slate-200 mb-8">
            {MAIN_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setMainTab(tab.id);
                  setOpenId(null);
                }}
                className={`flex-1 py-4 text-center text-[16px] font-bold transition-colors ${
                  mainTab === tab.id
                    ? "border-b-[3px] border-slate-900 text-slate-900"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div>
            {mainTab === "FAQ" && (
              <>
                {/* 💡 FAQ 소분류 탭 (쿠팡 스타일: 박스형 그리드) */}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-8">
                  {FAQ_CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => {
                        setFaqCategory(cat);
                        setOpenId(null);
                      }}
                      className={`py-3.5 text-sm font-medium border transition-colors ${
                        faqCategory === cat
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>

                {/* FAQ 리스트 */}
                <div className="border-t-2 border-slate-900">
                  {filteredFaqs.length > 0 ? (
                    filteredFaqs.map((faq) => (
                      <div key={`faq-${faq.id}`} className="border-b border-slate-200">
                        <button
                          onClick={() => toggleAccordion(faq.id)}
                          className="flex w-full items-center justify-between py-6 px-4 text-left hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            {/* 💡 쿠팡 스타일의 'Q' 마크 추가 */}
                            <span className="text-xl font-black text-rose-600 shrink-0">Q</span>
                            <span className={`text-[15px] ${openId === faq.id ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>
                              <span className="text-slate-500 mr-2">[{faq.category}]</span>
                              {faq.question}
                            </span>
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-300 ${openId === faq.id ? "rotate-180" : ""}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {openId === faq.id && (
                          <div className="bg-slate-50 px-4 py-8 text-[15px] text-slate-600 leading-relaxed md:pl-[3.25rem]">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="py-20 text-center text-slate-500">해당 카테고리의 자주 묻는 질문이 없습니다.</div>
                  )}
                </div>
              </>
            )}

            {mainTab === "NOTICE" && (
              <>
                {/* 공지사항 리스트 */}
                <div className="border-t-2 border-slate-900">
                  {isLoadingNotices ? (
                    <div className="py-20 text-center text-slate-500 font-medium">공지사항을 불러오는 중입니다...</div>
                  ) : notices.length > 0 ? (
                    notices.map((notice) => (
                      <div key={`notice-${notice.noticeId}`} className={`border-b border-slate-200 ${notice.isPinned === 'Y' ? 'bg-amber-50/20' : ''}`}>
                        <button
                          onClick={() => toggleAccordion(notice.noticeId)}
                          className="flex w-full items-center justify-between py-5 px-4 text-left hover:bg-slate-50 transition-colors"
                        >
                          {/* 💡 쿠팡 스타일: 제목 아래에 날짜 배치 */}
                          <div className="flex flex-col gap-1.5 min-w-0 flex-1 pr-4">
                            <span className={`text-[15px] truncate ${openId === notice.noticeId ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>
                              {notice.isPinned === 'Y' && <span className="font-bold text-amber-600 mr-2">[공지]</span>}
                              {notice.title}
                            </span>
                            <span className="text-sm text-slate-400">{notice.createdAt}</span>
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-300 ${openId === notice.noticeId ? "rotate-180" : ""}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {openId === notice.noticeId && (
                          <div className="bg-slate-50 border-t border-slate-100 px-6 py-8 text-[15px] text-slate-700 whitespace-pre-wrap leading-relaxed">
                            {notice.content}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="py-20 text-center text-slate-500">등록된 공지사항이 없습니다.</div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="mt-16 rounded-2xl bg-rose-50 p-8 text-center border border-rose-100">
            <h3 className="text-lg font-bold text-slate-800 mb-2">원하시는 답변을 찾지 못하셨나요?</h3>
            <p className="text-sm text-slate-600 mb-6">1:1 문의를 남겨주시면 신속하고 친절하게 안내해 드리겠습니다.</p>
            <button
              onClick={handleInquiryClick}
              className="inline-flex h-12 items-center justify-center rounded-full bg-rose-600 px-8 text-sm font-bold text-white hover:bg-rose-700 transition-colors"
            >
              1:1 문의 작성하기
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}